import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, X, FileText } from "lucide-react";
import {
  PortalPageShell,
  PortalSection,
  PortalPrimaryBtn,
  PortalOutlineBtn,
  PortalGhostBtn,
  PortalStatusChip,
  fmtMoney,
  fmtDate,
} from "@/pages/portal/PortalShared";
import { usePortal } from "@/pages/portal/PortalContext";

const ApproveModal = ({ open, quote, tourName, total, onCancel, onConfirm, loading }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="portal-approve-modal">
      <button aria-label="Close" className="absolute inset-0" style={{ background: "rgba(0,0,0,0.55)" }} onClick={onCancel} />
      <div
        className="relative w-full max-w-md rounded-md overflow-hidden"
        style={{ background: "var(--bg-elevated)", border: "1px solid var(--border-default)" }}
      >
        <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border-subtle)" }}>
          <div className="text-[15px] font-semibold" style={{ color: "var(--fg-primary)" }}>Approve Quote</div>
          <button onClick={onCancel} className="w-8 h-8 grid place-items-center rounded-md" style={{ color: "var(--fg-muted)" }}>
            <X size={18} />
          </button>
        </div>
        <div className="p-5">
          <div className="text-[14px]" style={{ color: "var(--fg-primary)" }}>
            Approve this quote? You&apos;re approving <strong>Quote v{quote?.version}</strong> for
            the <strong>{tourName}</strong> at <strong className="tabular">{total}</strong>.
          </div>
        </div>
        <div className="px-5 py-4 border-t flex items-center justify-end gap-2" style={{ borderColor: "var(--border-subtle)" }}>
          <PortalGhostBtn onClick={onCancel} data-testid="portal-approve-cancel">Cancel</PortalGhostBtn>
          <PortalPrimaryBtn onClick={onConfirm} disabled={loading} data-testid="portal-approve-confirm">
            {loading ? "Approving…" : "Approve Quote"}
          </PortalPrimaryBtn>
        </div>
      </div>
    </div>
  );
};

const DeclineModal = ({ open, onCancel, onConfirm, loading }) => {
  const [reason, setReason] = useState("");
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="portal-decline-modal">
      <button aria-label="Close" className="absolute inset-0" style={{ background: "rgba(0,0,0,0.55)" }} onClick={onCancel} />
      <div
        className="relative w-full max-w-md rounded-md overflow-hidden"
        style={{ background: "var(--bg-elevated)", border: "1px solid var(--border-default)" }}
      >
        <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border-subtle)" }}>
          <div>
            <div className="text-[15px] font-semibold" style={{ color: "var(--fg-primary)" }}>Decline Quote</div>
            <div className="text-[12.5px] mt-0.5" style={{ color: "var(--fg-muted)" }}>Tell us what you&apos;d like changed. (optional)</div>
          </div>
          <button onClick={onCancel} className="w-8 h-8 grid place-items-center rounded-md" style={{ color: "var(--fg-muted)" }}>
            <X size={18} />
          </button>
        </div>
        <div className="p-5">
          <textarea
            value={reason} onChange={(e) => setReason(e.target.value)}
            rows={5}
            placeholder="Optional feedback…"
            className="w-full rounded-md p-3 text-[13.5px] resize-y"
            style={{
              background: "var(--bg-inset)",
              color: "var(--fg-primary)",
              border: "1px solid var(--border-default)",
              WebkitAppearance: "none",
            }}
            data-testid="portal-decline-reason"
          />
        </div>
        <div className="px-5 py-4 border-t flex items-center justify-end gap-2" style={{ borderColor: "var(--border-subtle)" }}>
          <PortalGhostBtn onClick={onCancel} data-testid="portal-decline-cancel">Cancel</PortalGhostBtn>
          <PortalGhostBtn
            tone="critical"
            onClick={() => onConfirm(reason)}
            data-testid="portal-decline-confirm"
          >
            {loading ? "Declining…" : "Decline Quote"}
          </PortalGhostBtn>
        </div>
      </div>
    </div>
  );
};

export default function PortalQuote() {
  const { me, activeTour, quote, approveQuote, declineQuote } = usePortal();
  const [approveOpen, setApproveOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const totalStr = fmtMoney(quote?.customer_total);
  const totalDoc = quote?.pdf_file_id
    ? null
    : null; // pdf link comes from portalDocuments API

  const onApprove = async () => {
    setBusy(true);
    try {
      await approveQuote();
      toast.success("Quote approved. All Access has been notified.");
    } catch (e) {
      toast.error("Could not approve quote. Please contact your Account Manager.");
    } finally {
      setBusy(false);
      setApproveOpen(false);
    }
  };

  const onDecline = async (reason) => {
    setBusy(true);
    try {
      await declineQuote(reason);
      toast.success("Quote declined. Your tour information has been retained.");
    } catch (e) {
      toast.error("Could not decline quote. Please contact your Account Manager.");
    } finally {
      setBusy(false);
      setDeclineOpen(false);
    }
  };

  if (!quote) {
    return (
      <div className="max-w-[880px] mx-auto" data-testid="page-portal-quote">
        <PortalPageShell
          eyebrow="Transportation Proposal"
          title="Quote"
          subtitle="No quote has been issued for this tour yet."
        >
          <div
            className="rounded-md p-6 text-[13.5px]"
            style={{
              background: "var(--bg-surface)",
              border: "1px solid var(--border-default)",
              color: "var(--fg-secondary)",
            }}
          >
            Once your routing has been reviewed, All Access will issue a transportation proposal. It will appear here for your approval.
          </div>
        </PortalPageShell>
      </div>
    );
  }

  const statusTone =
    quote.status === "accepted" ? "lime" :
    quote.status === "declined" ? "critical" :
    "hold";

  return (
    <div className="max-w-[880px] mx-auto pb-24 md:pb-6" data-testid="page-portal-quote">
      <PortalPageShell
        eyebrow="Transportation Proposal"
        title={`Quote v${quote.version}`}
        subtitle={activeTour?.display_name}
        right={
          <PortalStatusChip tone={statusTone} testid="portal-quote-status-chip">
            {quote.status === "sent" ? "Awaiting Approval"
              : quote.status === "accepted" ? "Approved"
              : quote.status === "declined" ? "Declined"
              : (quote.status || "").toUpperCase()}
          </PortalStatusChip>
        }
      >
        <PortalSection label="Tour Dates">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.12em] font-semibold" style={{ color: "var(--fg-muted)" }}>Start</div>
              <div className="mt-1 text-[15px]" style={{ color: "var(--fg-primary)" }}>{fmtDate(activeTour?.start_date)}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.12em] font-semibold" style={{ color: "var(--fg-muted)" }}>End</div>
              <div className="mt-1 text-[15px]" style={{ color: "var(--fg-primary)" }}>{fmtDate(activeTour?.end_date)}</div>
            </div>
          </div>
        </PortalSection>

        <PortalSection label="Coach / Equipment">
          <div className="text-[13.5px]" style={{ color: "var(--fg-primary)" }}>
            {quote.equipment_summary || "Star Coach — full-service touring configuration."}
          </div>
        </PortalSection>

        {quote.customer_facing_notes && (
          <PortalSection label="Notes from All Access">
            <div
              className="rounded-md p-4 border-l-2"
              style={{
                background: "var(--bg-inset)",
                borderColor: "#1B4F8A",
                color: "var(--fg-primary)",
              }}
              data-testid="portal-quote-notes"
            >
              {quote.customer_facing_notes}
            </div>
          </PortalSection>
        )}

        <PortalSection label="Pricing Summary">
          <div className="text-[13.5px]" style={{ color: "var(--fg-secondary)" }}>
            Full line-item breakdown is included in your proposal document.
          </div>
        </PortalSection>

        <div className="py-6 mt-2 border-t-2" style={{ borderColor: "#B7E31D" }} data-testid="portal-quote-total-block">
          <div className="flex items-end justify-between flex-wrap gap-2">
            <div className="uppercase text-[11.5px] tracking-[0.14em] font-semibold" style={{ color: "var(--fg-muted)" }}>
              Total
            </div>
            <div
              className="tabular text-[36px] md:text-[42px] font-semibold leading-none"
              style={{ color: "var(--fg-primary)", fontFamily: "'Montserrat', 'Inter', sans-serif" }}
              data-testid="portal-quote-total"
            >
              {totalStr}
            </div>
          </div>
        </div>

        <div className="hidden md:flex items-center justify-end gap-2 mt-4" data-testid="portal-quote-actions">
          {quote.status === "sent" && (
            <>
              <PortalGhostBtn tone="critical" onClick={() => setDeclineOpen(true)} data-testid="portal-quote-decline-btn">
                Decline
              </PortalGhostBtn>
              <PortalPrimaryBtn onClick={() => setApproveOpen(true)} data-testid="portal-quote-approve-btn">
                Approve Quote
              </PortalPrimaryBtn>
            </>
          )}
          {quote.status === "accepted" && (
            <div className="flex items-center gap-2 text-[13.5px]" style={{ color: "var(--fg-primary)" }}
                 data-testid="portal-quote-accepted-note">
              <CheckCircle2 size={16} style={{ color: "#B7E31D" }} />
              Quote Approved. All Access has been notified and will continue the booking process.
            </div>
          )}
          {quote.status === "declined" && (
            <div className="flex items-center gap-2 text-[13.5px]" style={{ color: "var(--fg-primary)" }}
                 data-testid="portal-quote-declined-note">
              <XCircle size={16} style={{ color: "#B83C3C" }} />
              Quote Declined. Your quote and tour information have been retained. All Access can revise the proposal with you.
            </div>
          )}
        </div>
      </PortalPageShell>

      {/* Mobile sticky action footer */}
      {quote.status === "sent" && (
        <div
          className="md:hidden fixed left-0 right-0 z-30"
          style={{
            bottom: "calc(3.5rem + env(safe-area-inset-bottom, 0px))",
            background: "var(--bg-elevated)",
            borderTop: "1px solid var(--border-default)",
          }}
          data-testid="portal-quote-mobile-footer"
        >
          <div className="p-3 flex gap-2">
            <button
              onClick={() => setDeclineOpen(true)}
              data-testid="portal-quote-decline-btn-mobile"
              className="flex-1 h-11 rounded-md text-[13.5px] font-medium"
              style={{ color: "#B83C3C", background: "transparent", border: "1px solid var(--border-strong)" }}
            >
              Decline
            </button>
            <button
              onClick={() => setApproveOpen(true)}
              data-testid="portal-quote-approve-btn-mobile"
              className="flex-1 h-11 rounded-md text-[13.5px] font-semibold"
              style={{ background: "#1B4F8A", color: "#fff" }}
            >
              Approve Quote
            </button>
          </div>
        </div>
      )}

      <ApproveModal
        open={approveOpen}
        quote={quote}
        tourName={activeTour?.display_name || "your tour"}
        total={totalStr}
        loading={busy}
        onCancel={() => setApproveOpen(false)}
        onConfirm={onApprove}
      />
      <DeclineModal
        open={declineOpen}
        loading={busy}
        onCancel={() => setDeclineOpen(false)}
        onConfirm={onDecline}
      />
    </div>
  );
}
